
// Use browser-compatible crypto instead of Node.js crypto
// This uses the Web Crypto API that's available in modern browsers

// Constants for encryption
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 32; // 256 bits
const SALT_LENGTH = 16;
const IV_LENGTH = 12; // 96 bits is recommended for AES-GCM
const TAG_LENGTH = 16; // 128 bits authentication tag

// Helper function to convert string to Uint8Array
const strToUint8Array = (str: string): Uint8Array => {
  return new TextEncoder().encode(str);
};

// Helper function to convert Uint8Array to string
const uint8ArrayToStr = (buf: Uint8Array): string => {
  return new TextDecoder().decode(buf);
};

// Helper function to convert base64 to Uint8Array
const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// Helper function to convert Uint8Array to base64
const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  return btoa(String.fromCharCode.apply(null, Array.from(bytes)));
};

// Helper to generate a key from password and salt using the Web Crypto API
const getKey = async (password: string, salt: Uint8Array): Promise<CryptoKey> => {
  const passwordBytes = strToUint8Array(password);
  
  // Create a key from the password
  const baseKey = await window.crypto.subtle.importKey(
    'raw',
    passwordBytes,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  
  // Derive a key for AES-GCM
  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    baseKey,
    { name: ALGORITHM, length: KEY_LENGTH * 8 },
    false,
    ['encrypt', 'decrypt']
  );
};

// Encrypt data using AES-GCM and Web Crypto API
export const encryptData = async (data: any): Promise<string> => {
  try {
    // Convert data to JSON string
    const dataString = JSON.stringify(data);
    
    // Generate random salt and IV
    const salt = window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
    // Get encryption key
    const key = await getKey('SECURE_BLUETOOTH_KEY', salt);
    
    // Encrypt the data
    const dataBytes = strToUint8Array(dataString);
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      key,
      dataBytes
    );
    
    // Combine salt, IV, and encrypted data
    const result = new Uint8Array(SALT_LENGTH + IV_LENGTH + encryptedData.byteLength);
    result.set(salt, 0);
    result.set(iv, SALT_LENGTH);
    result.set(new Uint8Array(encryptedData), SALT_LENGTH + IV_LENGTH);
    
    // Return as base64 string
    return uint8ArrayToBase64(result);
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

// Decrypt data using AES-GCM and Web Crypto API
export const decryptData = async (encryptedBase64: string): Promise<any> => {
  try {
    // Convert base64 to Uint8Array
    const encryptedBuffer = base64ToUint8Array(encryptedBase64);
    
    // Extract salt, IV, and encrypted data
    const salt = encryptedBuffer.slice(0, SALT_LENGTH);
    const iv = encryptedBuffer.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const encrypted = encryptedBuffer.slice(SALT_LENGTH + IV_LENGTH);
    
    // Get decryption key
    const key = await getKey('SECURE_BLUETOOTH_KEY', salt);
    
    // Decrypt the data
    const decryptedData = await window.crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      key,
      encrypted
    );
    
    // Convert back to string and parse JSON
    const decryptedString = uint8ArrayToStr(new Uint8Array(decryptedData));
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data');
  }
};

// Generate a cryptographically secure signature
export const generateSignature = async (data: any, privateKey: string): Promise<string> => {
  try {
    const dataString = JSON.stringify(data);
    const messageBytes = strToUint8Array(dataString + privateKey);
    
    // Create SHA-256 hash
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', messageBytes);
    
    // Convert to base64
    return uint8ArrayToBase64(new Uint8Array(hashBuffer));
  } catch (error) {
    console.error('Signature generation error:', error);
    throw new Error('Failed to generate signature');
  }
};

// Verify signature authenticity
export const verifySignature = async (data: any, signature: string, publicKey: string): Promise<boolean> => {
  try {
    const expectedSignature = await generateSignature(data, publicKey);
    return signature === expectedSignature;
  } catch (error) {
    console.error('Signature verification error:', error);
    return false;
  }
};
